import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Divider,
  Grid,
  ResponsiveSection,
  Stack,
  useScreenSize,
} from '@stitch-fix/mode-react';
import { useViewEvent, ViewEventProvider } from '@stitch-fix/event-reporter';
import { useInView } from 'react-intersection-observer';
import { recordMark } from '@stitch-fix/client-analytics-reporter';
import Cookies from 'js-cookie';
import uuid from 'react-uuid';
import ErrorBoundary from '../shared/ErrorBoundary/ErrorBoundary';
import ProductAttributes from '../ProductAttributes';
import StylistChatPdpPrompt from '../StylistChatPdpPrompt/StylistChatPdpPrompt';
import ProductImageGallery from '../ProductImageGallery';
import ProductTitle from '../ProductTitle';
import ProductSelection from '../ProductSelection';
import BusinessLineMismatchBanner from '../BusinessLineMismatchBanner';
import SimilarItemsLink from '../SimilarItemsLink/SimilarItemsLink';
import BuyNow from '../Gated/src/components/shared/BuyNow/BuyNow';
import AddToBagButton from '../AddToBagButton';
import MoveToBag from '../Gated/src/components/savedItems/MoveToBag/MoveToBag';
import HideItemLink from '../HideItemLink/HideItemLink';
import DiscoverSkusCollectionCarousel from '../Gated/src/components/DiscoverSkusCollectionCarousel';
import { ShopCategoriesCarousel } from '../ShopCategoriesCarousel';
import WaysToWearItCarousel from '../WaysToWearItCarousel';
import { useProductDetailsPageContext } from './ProductDetailsPageContext/ProductDetailsPageContext';
import { sendCount, sendLog } from '../../utils/sendMetrics';
import { trackPDPViewEvent } from '../Gated/src/components/ProductDetailsPage/utils';
import {
  fireGtmPageViewEvent,
  trackMismatchBizLine,
  trackPageView,
} from './ProductDetailsPage.tracking';
import OutfitItemSelect from '../OutfitItemSelect/OutfitItemSelect';
import InventoryFailureAlert from '../InventoryFailureAlert';
import RateLimitFailureAlert from '../RateLimitFailureAlert';
import CreditIncentivesBanner from '../CreditIncentivesBanner';
import BagErrorBanner from '../BagErrorBanner/BagErrorBanner';
import ShippingDetailsForPDP from '../ShippingDetailsForPDP/ShippingDetailsForPDP';
import BuyNowButton from '../BuyNowButton';
import AnchoredFixPromo from '../AnchoredFixPromo';
import NewEntryPoints from '../NewEntryPoints';
import StyleQuizAd from '../StyleQuizAd';
import ErrorUi from '../shared/ErrorUi';
import pdpShould, {
  MINIMUM_ITEMS_TO_RENDER,
  RENDER_ANCHORED_FIX_PURCHASE_OPTIONS,
} from './pdpShould';
import './ProductDetailsPage.module.scss';
import ContentModule from '../ContentModule';
import FixPromotion from '../FixPromotion/FixPromotion';
import FixPurchaseOptions from '../Gated/src/components/FixPurchaseOptions';
import { MatchingSetCarousel } from '../MatchingSetCarousel/MatchingSetCarousel';

const ProductDetailsPage = () => {
  const params = new URLSearchParams(window.location.search);
  const screenSize = useScreenSize();
  const [state] = useProductDetailsPageContext();

  // Check the query parameter origin value. If the value is 'anchoritem', it indicates
  // the redirection is from the anchor item purchasing page. In that case, we need to
  // pre-select the 'Get it in a Fix' option.
  const origin = params.get('origin');
  const purchaseOption = origin === 'anchoritem' ? 'GET_IN_FIX' : 'NONE';

  const [pdpInViewRef, pdpInView] = useInView({
    triggerOnce: true,
  });

  const setPdpRefs = useCallback(
    node => {
      pdpInViewRef(node);
    },
    [pdpInViewRef],
  );

  useEffect(() => {
    if (pdpInView) {
      if (state.gated) {
        trackPDPViewEvent(state);
      } else {
        trackPageView(state);
        recordMark('ungated_pdp_page_view', {
          shop_product_model: !!state.externalProductId,
        });

        if (state.clientBusinessLineIsMismatched) {
          trackMismatchBizLine(state);
        }
      }
    }
  }, [pdpInView, state]);

  const similarItemsRef = useRef(null);

  const setSimilarItemsRefs = useCallback(node => {
    similarItemsRef.current = node;
  }, []);

  useEffect(() => {
    const recommendedSkuIdIsMissing = !params.has('recommended-sku-id');
    const skuIdIsPresent = params.has('sku-id');

    if (recommendedSkuIdIsMissing && skuIdIsPresent && state.gated) {
      window.history.pushState(
        state,
        state.title,
        `${window.location.toString()}&recommended-sku-id=${params.get(
          'sku-id',
        )}`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // used to ensure we only fire the page view event once, even after dependencies update
  const [gtmPageViewEventFired, setGtmPageViewEventFired] = useState(false);

  useEffect(() => {
    // we want to make sure these values have been loaded before we fire the page view event
    if (
      !gtmPageViewEventFired &&
      (state.selectedVariant || // accounts for 'selected' SPM pages
        state.fallbackVariant) && // accounts for 'unselected' SPM pages
      state.formattedPrice &&
      (state.productSkuId || state.shopProduct)
    ) {
      setGtmPageViewEventFired(true);
      fireGtmPageViewEvent(state);
    }
  }, [state, gtmPageViewEventFired]);

  // Apply buy button spacing class to body for fixed mobile footer
  useEffect(() => {
    if (state.gated) {
      document.body.classList.add('buy-button-placeholder');
    }
  }, [state.gated]);

  // HACK: This should be a side effect, but currently side effects do not have access to computed state.
  useEffect(() => {
    if (!state.selectedVariantURL) {
      return;
    }

    const currentPath = window.location.pathname;

    let newPath;

    newPath = state.selectedVariantURL
      .replace('https://www.stitchfix.com', '')
      .replace('product', state.urlScope);

    const currentPathIsTheSame = newPath.includes(currentPath);

    const newPathHasSkuId = newPath.includes('sku-id=');
    const currentSkuIdIsTheSame = newPath.includes(
      `sku-id=${params.get('sku-id')}`,
    );

    params.delete('sku-id');
    params.delete('color');
    params.delete('size');
    params.delete('inseam');

    if (params.toString().length > 0) {
      const delimiter = newPath.includes('?') ? '&' : '?';

      newPath = newPath.concat(`${delimiter}${params.toString()}`);
    }

    const shouldUpdatePath =
      !currentPathIsTheSame || (newPathHasSkuId && !currentSkuIdIsTheSame);

    if (shouldUpdatePath) {
      // **************** Product Cache Testing **************** //
      const regex = /product-cache/;

      if (currentPath !== undefined && currentPath.match(regex)) {
        const visitorId = Cookies.get('visitor_uuid');

        if (!visitorId) {
          sendLog(`Unable to find visitorId cookie: ${document.cookie}`);
          // eslint-disable-next-line no-console
          console.log('Setting visitor_uuid cookie');
          const uuid_value = uuid();

          Cookies.set('visitor_uuid', uuid_value);
        } else {
          // eslint-disable-next-line no-console
          console.log(`visitor_uuid is defined: ${visitorId}`);
        }
        // **************** End Product Cache Testing ****************//
      } else {
        window.history.replaceState(null, '', newPath);
      }
    }
    // We only want to run this hook when url dependencies update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedVariantURL]);

  const { ref, viewContext } = useViewEvent({
    schema: 'sku_view',
    name: 'pdp',
    sku_id: (state.selectedSkuId || state.fallbackSkuId || '').toString(),
    // Avoid passing down a `false` value for inventory statuses to child events until
    // that data has actually loaded
    limited_stock: state.priceAndInventoryData ? state.isSkuLimitedStock : null,
    out_of_stock: state.priceAndInventoryData ? state.isSkuOutOfStock : null,
    is_anchorable: state.shopPurchaseOptionsLoading
      ? null
      : !!state.shopPurchaseOptions,
  });

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // Redirect to a 404 page for kids skus in order to block kid's PDPs from being viewed
  if (
    (window as any).gon.env !== 'development' && // redirect is annoying in local dev, so removing local functionality
    state.productBusinessLine === 'KIDS' &&
    !state.gated
  ) {
    sendCount('product-query.error.kids_sku');
    window.location.replace(`${window.location.pathname}/not_found`);
  }
  /* eslint-enable */

  if (pdpShould('RENDER_ERROR_UI', state)) {
    return <ErrorUi />;
  }

  return (
    <ViewEventProvider viewContext={viewContext}>
      <Box
        ref={
          pdpShould('TRIGGER_LEGACY_VIEW_EVENT', state) ? setPdpRefs : undefined
        }
        data-testid="product-page"
      >
        <Box ref={pdpShould('TRIGGER_VIEW_EVENT', state) ? ref : undefined}>
          {pdpShould('RENDER_INVENTORY_FAILURE_ALERT', state) && (
            <InventoryFailureAlert ref={similarItemsRef} />
          )}
          {pdpShould('RENDER_RATE_LIMIT_FAILURE_ALERT', state) && (
            <RateLimitFailureAlert />
          )}
          {pdpShould('RENDER_CREDIT_INCENTIVES_BANNER', state) && (
            <CreditIncentivesBanner />
          )}
          <ResponsiveSection
            px={{ lg: 2, xxl: 0, sm: 1 }}
            width="xxlarge"
            mt={{ sm: 1, lg: 1.5 }}
          >
            {pdpShould('RENDER_BAG_ERROR_BANNER', state) && <BagErrorBanner />}
            <Grid
              spacing={{ lg: 'wide', sm: 'standard' }}
              columns={{ lg: [7, 5], sm: 1 }}
            >
              {pdpShould('RENDER_OUTFIT_ITEM_SELECT', state, screenSize) ? (
                <OutfitItemSelect />
              ) : null}
              {pdpShould(
                'RENDER_PRODUCT_TITLE_IN_SMALL_SCREEN_POSITION',
                state,
                screenSize,
              ) && <ProductTitle />}
              {pdpShould(
                'RENDER_HIDE_ITEM_LINK_IN_SMALL_SCREEN_POSITION',
                state,
                screenSize,
              ) && <HideItemLink />}
              <div styleName="thumbnail-gallery">
                <ProductImageGallery />
              </div>
              <div>
                {pdpShould(
                  'RENDER_PRODUCT_TITLE_DESKTOP_POSITION',
                  state,
                  screenSize,
                ) && <ProductTitle />}
                {pdpShould(
                  'RENDER_HIDE_ITEM_LINK_IN_DESKTOP_POSITION',
                  state,
                  screenSize,
                ) && <HideItemLink />}
                <ErrorBoundary displayErrorMessaging={false}>
                  <div>
                    <ProductSelection />
                    {pdpShould(
                      'RENDER_BUSINESS_LINE_MISMATCH_BANNER',
                      state,
                    ) && (
                      <div data-testid="business-line-mismatch-banner">
                        <BusinessLineMismatchBanner />
                      </div>
                    )}
                    <div>
                      {pdpShould('RENDER_SIMILAR_ITEMS_LINK', state) && (
                        <SimilarItemsLink ref={similarItemsRef} />
                      )}
                      {pdpShould(RENDER_ANCHORED_FIX_PURCHASE_OPTIONS, state) &&
                        state.shopPurchaseOptions && (
                          <Box mb={1} pt={0.5} maxWidth="435px">
                            <FixPurchaseOptions
                              isItemSaved={pdpShould(
                                'RENDER_MOVE_TO_BAG_BUTTON',
                                state,
                              )}
                              isOutOfStock={state.isSkuOutOfStock}
                              fixPurchaseOptions={state.shopPurchaseOptions}
                              selectedOptionVariant={purchaseOption}
                            />
                          </Box>
                        )}
                      {pdpShould('RENDER_SHIPPING_DETAILS', state) && (
                        <ShippingDetailsForPDP />
                      )}
                    </div>
                    {pdpShould('RENDER_CTAS', state) && (
                      <Box maxWidth="396px">
                        <Grid
                          mb={{ sm: 2, md: 1.5 }}
                          columns={{
                            sm: pdpShould('RENDER_TWO_CTA_BUTTONS', state)
                              ? 2
                              : 1,
                            md: 2,
                          }}
                          spacing={0.75}
                        >
                          <>
                            {pdpShould(
                              'RENDER_GATED_BUY_NOW_BUTTON',
                              state,
                            ) && <BuyNow />}
                            {pdpShould(
                              'RENDER_UNGATED_BUY_NOW_BUTTON',
                              state,
                            ) && <BuyNowButton />}
                            {pdpShould('RENDER_MOVE_TO_BAG_BUTTON', state) && (
                              <MoveToBag />
                            )}
                            {pdpShould('RENDER_ADD_TO_BAG_BUTTON', state) && (
                              <AddToBagButton />
                            )}
                          </>
                        </Grid>
                      </Box>
                    )}
                    {pdpShould('RENDER_ANCHORED_FIX_PROMO', state) && (
                      <AnchoredFixPromo skuId={state.productSkuId} />
                    )}
                    {pdpShould('RENDER_NEW_ENTRY_POINTS', state) && (
                      <NewEntryPoints />
                    )}
                  </div>
                </ErrorBoundary>
                {pdpShould(
                  'RENDER_PRODUCT_ATTRIBUTES_IN_DESKTOP_POSITION',
                  state,
                  screenSize,
                ) && <ProductAttributes />}
                {pdpShould(
                  'RENDER_MATCHING_SET_UNDER_DETAILS',
                  state,
                  screenSize,
                ) && (
                  <Grid>
                    <MatchingSetCarousel />
                  </Grid>
                )}
              </div>
            </Grid>
          </ResponsiveSection>
          {/* Below is the layout of bottom content. Each carousel or promotional is wrapped in a Content
              Module to standardized padding and the Stack controls the vertical spacing. */}
          <Stack spacing={{ sm: 2, md: 2 }} mb={{ sm: 0, md: 4 }}>
            {pdpShould(
              'RENDER_MATCHING_SET_WITH_SHOPPABLE_CONTENT',
              state,
              screenSize,
            ) && (
              <ContentModule>
                <MatchingSetCarousel />
              </ContentModule>
            )}
            <ContentModule>
              <div id="ways-to-wear-it-carousel">
                {pdpShould(
                  'RENDER_WTWI_CAROUSEL_MOBILE_DIVIDER',
                  state,
                  screenSize,
                ) && <Divider variant="section" color="light-gray" />}
                <Box
                  mt={
                    pdpShould('RENDER_SPACE_ABOVE_WTWI', state, screenSize)
                      ? 2
                      : 0
                  }
                >
                  <WaysToWearItCarousel />
                </Box>
              </div>
            </ContentModule>
            {pdpShould(
              'RENDER_PRODUCT_ATTRIBUTES_IN_SMALL_SCREEN_POSITION',
              state,
              screenSize,
            ) && (
              <ContentModule>
                <ProductAttributes />
              </ContentModule>
            )}
          </Stack>
          {pdpShould('RENDER_STYLIST_CHAT_PDP_PROMPT', state, screenSize) && (
            <ContentModule>
              <StylistChatPdpPrompt />
            </ContentModule>
          )}
          <Stack
            spacing={{ sm: 3, md: 4 }}
            mb={{ sm: 0, md: 4.5 }}
            mt={{ sm: 1, md: 0 }}
          >
            {pdpShould('RENDER_DISCOVER_SKUS_COLLECTION_CAROUSELS', state) &&
              state.recommendedCollections?.map(content => {
                const carouselItems = content.shopProducts.nodes;

                /**
                 * Logic to render carousel only if it has more than 3 items.
                 * This will prevent from rendering ghost margins in the carousel.
                 */
                return carouselItems.length > MINIMUM_ITEMS_TO_RENDER ? (
                  <ContentModule key={content.id}>
                    <div
                      ref={
                        content.treatment === 'similar_items'
                          ? setSimilarItemsRefs
                          : null
                      }
                    >
                      <DiscoverSkusCollectionCarousel
                        key={content.id}
                        content={content}
                        minimumItemsToRender={MINIMUM_ITEMS_TO_RENDER}
                      />
                    </div>
                  </ContentModule>
                ) : null;
              })}
            {pdpShould('RENDER_SHOP_CATEGORIES_CAROUSEL', state) && (
              <ContentModule>
                <ShopCategoriesCarousel />
              </ContentModule>
            )}
            {pdpShould('RENDER_STYLE_QUIZ_AD', state) && (
              <ContentModule>
                <StyleQuizAd />
              </ContentModule>
            )}
            {pdpShould('RENDER_FIX_PROMOTION', state) && (
              <ContentModule>
                <FixPromotion />
              </ContentModule>
            )}
          </Stack>
        </Box>
      </Box>
    </ViewEventProvider>
  );
};

export default ProductDetailsPage;
